<template>
  <baidu-map
    class="bm-view"
    :high-resolution="true"
    :continuous-zoom="true"
    :center="center"
    :zoom="zoom"
    @ready="handlerBMap">
    <bm-scale anchor="BMAP_ANCHOR_BOTTOM_LEFT" />
    <div
      v-if="isLoading"
      class="map-loader-container">
      <div>
        {{ capitalize($t('messages.initializingMap')) }}
      </div>
    </div>
    <bm-map-type
      v-if="enableMapStyleControl"
      :map-types="['BMAP_NORMAL_MAP', 'BMAP_HYBRID_MAP']"
      anchor="BMAP_ANCHOR_BOTTOM_RIGHT" />
    <bm-navigation anchor="BMAP_ANCHOR_TOP_RIGHT" />
  </baidu-map>
</template>
<script>
  import geojsonExtent from '@mapbox/geojson-extent';
  import { capitalize } from 'vue';

  import Colors from '@/Colors';

  export default {
    props: {
      enableMapStyleControl: {
        type: Boolean,
        default: false,
      },
      geofences: {
        type: Array,
      },
      activeGeofence: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        center: null,
        zoom: 1,
        map: null,
        BMap: null,
        isLoading: true,
        markers: [],
        polygons: [],
        bounds: [],
        radiusCircles: [],
      };
    },
    watch: {
      activeGeofence() {
        if (this.activeGeofence != null) {
          this.bounds = [
            { lng: this.activeGeofence.boundsSwLng, lat: this.activeGeofence.boundsSwLat },
            { lng: this.activeGeofence.boundsNeLng, lat: this.activeGeofence.boundsNeLat },
          ];
          this.zoomToGeofence();
        } else {
          this.infoWindowClose();
          this.shapesBoundingBox();
        }
      },
      geofences() {
        if (this.geofences != null && this.map != null) {
          this.updateGeofenceDataSources();
        }
      },
    },
    methods: {
      capitalize,
      handlerBMap({ BMap, map }) {
        this.isLoading = false;
        this.BMap = BMap;
        this.map = map;
        if (this.geofences != null && this.geofences.length > 0) {
          this.updateGeofenceDataSources();
        } else {
          this.map.centerAndZoom('中国');
        }
        if (this.activeGeofence != null) {
          this.bounds = [
            { lng: this.activeGeofence.boundsSwLng, lat: this.activeGeofence.boundsSwLat },
            { lng: this.activeGeofence.boundsNeLng, lat: this.activeGeofence.boundsNeLat },
          ];
        }
      },
      updateGeofenceDataSources() {
        if (!this.map) return;

        this.map.clearOverlays();

        this.geofences.forEach((geofence) => {
          const icon = new this.BMap.Icon('/assets/images/map/pin-x1.png', new this.BMap.Size(12, 12), {
            anchor: new this.BMap.Size(6, 6),
            infoWindowAnchor: new this.BMap.Size(6, 0),
          });

          const pointCoords = new this.BMap.Point(geofence.lng, geofence.lat);

          const circle = new this.BMap.Marker(pointCoords, {
            icon,
          });

          circle.addEventListener('click', () => {
            this.onPointClicked(geofence);
          });
          this.map.addOverlay(circle);
          this.markers.push(circle);

          if (geofence.isPoint) {
            const radiusCircle = new this.BMap.Circle(pointCoords, geofence.radius * 1000, {
              strokeColor: Colors.geofences,
              fillColor: Colors.geofences,
              strokeWeight: 1,
              strokeOpacity: 0.8,
              fillOpacity: 0.2,
              strokeStyle: 'solid',
            });
            this.map.addOverlay(radiusCircle);
            this.radiusCircles.push(radiusCircle);
          }

          if (geofence.geoJson != null) {
            let geofencePolygonPath = [];
            if (typeof geofence.geoJson === 'string') {
              // drawing multiple polygons at one location when a polygon is appended
              const geofenceObject = JSON.parse(geofence.geoJson);
              geofenceObject.coordinates.forEach((polygon) => {
                geofencePolygonPath = polygon[0].map((point) => new this.BMap.Point(point[0], point[1]));
                const geofencePolygon = new this.BMap.Polygon(geofencePolygonPath, {
                  strokeColor: Colors.geofences,
                  fillColor: Colors.geofences,
                  strokeWeight: 1,
                  strokeOpacity: 0.8,
                  fillOpacity: 0.2,
                  strokeStyle: 'solid',
                });
                this.map.addOverlay(geofencePolygon);
                this.polygons.push(geofencePolygon);
              });
            } else {
              if ('geometry' in geofence.geoJson) {
                geofencePolygonPath = geofence.geoJson.geometry.coordinates[0].map(
                  (point) => new this.BMap.Point(point[0], point[1]),
                );
              } else {
                geofencePolygonPath = geofence.geoJson.coordinates[0].map(
                  (point) => new this.BMap.Point(point[0], point[1]),
                );
              }

              const geofencePolygon = new this.BMap.Polygon(geofencePolygonPath, {
                strokeColor: Colors.geofences,
                fillColor: Colors.geofences,
                strokeWeight: 1,
                strokeOpacity: 0.8,
                fillOpacity: 0.2,
                strokeStyle: 'solid',
              });
              this.map.addOverlay(geofencePolygon);
              this.polygons.push(geofencePolygon);
            }
          }
        });
        if (this.activeGeofence != null) {
          this.zoomToGeofence();
        } else {
          this.fitAllGeofencesIntoView();
        }
      },
      fitAllGeofencesIntoView() {
        const data = {
          type: 'FeatureCollection',
          features: this.geofences != null ? this.geofences.map((x) => x.feature) : [],
        };
        if (data != null && data.features != null && data.features.length !== 0) {
          const extents = data.features.map((x) => geojsonExtent(x.geometry));

          const minLat = Math.min(...extents.map((x) => x[1]));
          const maxLat = Math.max(...extents.map((x) => x[3]));
          const minLng = Math.min(...extents.map((x) => x[0]));
          const maxLng = Math.max(...extents.map((x) => x[2]));

          this.bounds = [
            { lng: minLng, lat: minLat },
            { lng: maxLng, lat: maxLat },
          ];

          if (!this.map) return;

          this.zoomToGeofence();
          const viewport = this.map.getViewport(this.bounds);
          this.mapCenter = viewport.center;
          this.map.centerAndZoom(this.mapCenter);
        }
      },
      zoomToGeofence() {
        if (!this.map) return;
        this.map.setViewport(this.bounds);
      },
      onPointClicked(geofence) {
        this.$emit('pointClicked', geofence);
      },
    },
  };
</script>
